import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

export function initSentry() {
  const isProd = import.meta.env.PROD;

  Sentry.init({
    dsn: "https://5ac098f4aea241ad5853b17640dade48@o4510895490531328.ingest.us.sentry.io/4510895520088064",
    environment: isProd ? "production" : "development",

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],

    tracesSampleRate: isProd ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: true,

    beforeSend(event) {
      // Remove sensitive cookies
      if (event.request?.cookies) {
        delete event.request.cookies;
      }

      // Remove sensitive query params
      if (event.request?.query_string) {
        const params = new URLSearchParams(event.request.query_string);
        ["token", "api_key", "apiKey", "apiSecret", "password"].forEach((key) =>
          params.delete(key)
        );
        event.request.query_string = params.toString();
      }

      // Remove sensitive headers
      if (event.request?.headers) {
        delete event.request.headers["Authorization"];
        delete event.request.headers["Cookie"];
      }

      return event;
    },

    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      "Non-Error promise rejection captured",
      "chrome-extension://",
      "moz-extension://",
      "NetworkError",
      "Failed to fetch",
      // Sentry SDK internal error (v7 bug in views.js polling)
      "has no method 'updateFrom'",
      "updateFrom",
    ],
  });

  console.log("[Sentry] Initialized in", isProd ? "PRODUCTION" : "DEVELOPMENT", "mode");
}

/** Identify user after login */
export function identifySentryUser(
  userId: string,
  email?: string,
  metadata?: Record<string, unknown>
) {
  Sentry.setUser({
    id: userId,
    email,
    username: (metadata?.username as string) || undefined,
  });
}

/** Clear user on logout */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/** Capture a custom error */
export function captureSentryError(error: Error, context?: Record<string, unknown>) {
  Sentry.captureException(error, {
    contexts: { custom: context as Record<string, unknown> },
    level: "error",
  });
}

/** Capture a custom message */
export function captureSentryMessage(
  message: string,
  level: "info" | "warning" | "error" = "info",
  context?: Record<string, unknown>
) {
  Sentry.captureMessage(message, {
    level,
    contexts: { custom: context as Record<string, unknown> },
  });
}

/** Add a breadcrumb */
export function addSentryBreadcrumb(
  message: string,
  category: string,
  level: "debug" | "info" | "warning" | "error" = "info",
  data?: Record<string, unknown>
) {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
    timestamp: Date.now() / 1000,
  });
}

/** Set a tag */
export function setSentryTag(key: string, value: string) {
  Sentry.setTag(key, value);
}

/** Set extra context */
export function setSentryContext(name: string, context: Record<string, unknown>) {
  Sentry.setContext(name, context);
}
